import React, { useState, useCallback, useRef, useMemo } from 'react';
import moment from 'moment';
import { toast as toastify } from 'react-toastify';
import { Toast } from '@packages/ui/composites/Toast';
import { Button } from '@packages/ui/primitives/Button';
import { Typography } from '@packages/ui/primitives/Typography';
import { Search } from '@packages/ui/composites/Search';
import { Filter } from '@packages/ui/composites/Filter';
import { Select } from '@packages/ui/composites/Select';
import { DatePicker } from '@packages/ui/composites/DatePicker';
import { Icon } from '@packages/ui/primitives/Icon';
import { Accordion } from '@packages/ui/composites/Accordion';
import { DATE_FORMAT } from '@packages/shared/constants';
import { parseDateLocal } from '@packages/core';
import { normalizeMultiSectionSchema } from '../schema/multiSectionNormalizer';
import type { ExternalTableSchema, InternalMultiSectionSchema } from '../schema/types';
import type { DynamicTableV2Props } from '../types/DynamicTableV2.types';
import { ToolbarDropdownButton } from './ToolbarDropdownButton';

interface MultiSectionRendererProps extends DynamicTableV2Props {
  DynamicTableV2Component: React.ComponentType<DynamicTableV2Props>;
}

export function MultiSectionRenderer(props: MultiSectionRendererProps): JSX.Element {
  const {
    schema,
    axiosInstance,
    navigate,
    checkPermission,
    onActionIntercept,
    envTokens,
    DynamicTableV2Component,
  } = props;

  const normalizedRef = useRef<InternalMultiSectionSchema | null>(null);
  if (normalizedRef.current === null) {
    normalizedRef.current = normalizeMultiSectionSchema(schema);
  }
  const normalized = normalizedRef.current;

  const [searchText, setSearchText] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(
    () => new Set(normalized.initialExpandedSections),
  );

  const toggleSection = useCallback((id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleFilterChange = useCallback((name: string, value: unknown) => {
    setFilterValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearchText('');
    setFilterValues({});
  }, []);

  const sc = normalized.sharedControls;
  const hasFilters = sc.filters.length > 0;
  const hasToolbar = sc.toolbar.primary !== null || sc.toolbar.secondary.length > 0;
  const hasImport = sc.import?.downloadTemplate || sc.import?.uploadAction;

  const sharedQueryParams = useMemo(() => {
    const params: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(filterValues)) {
      if (val !== undefined && val !== null && val !== '') {
        params[key] = val;
      }
    }
    return params;
  }, [filterValues]);

  return (
    <div data-testid='multi-section-table'>
      {normalized.title && (
        <div className='mb-4'>
          <Typography variant='h3'>{normalized.title}</Typography>
          {normalized.description && (
            <Typography variant='body-s' color='var(--ui-text-muted)'>
              {normalized.description}
            </Typography>
          )}
        </div>
      )}

      <div className='mb-6 flex items-center gap-4'>
        <div className='flex-1 max-w-md'>
          {sc.search.enabled && (
            <Search
              value={searchText}
              onValueChange={setSearchText}
              onClear={() => setSearchText('')}
              semanticId='multiSectionSearch'
              placeholder={sc.search.placeholder ?? 'Search across all awards...'}
            />
          )}
        </div>
        <div className='flex items-center gap-2'>
          {hasFilters && (
            <Filter.Root open={filterOpen} onOpenChange={setFilterOpen}>
              <Filter.Trigger />
              <Filter.Panel size='lg'>
                <Filter.Header>
                  <Typography variant='body-xs'>FILTER BY:</Typography>
                </Filter.Header>
                <Filter.Grid>
                  {sc.filters.map((f) => {
                    if (f.type === 'date' || f.type === 'dateRange') {
                      const dateVal = filterValues[f.name]
                        ? parseDateLocal(String(filterValues[f.name]))
                        : null;
                      return (
                        <DatePicker
                          key={f.name}
                          name={f.name}
                          value={dateVal}
                          onChange={(date: Date | null) => {
                            handleFilterChange(
                              f.name,
                              date ? moment(date).format(DATE_FORMAT) : ''
                            );
                          }}
                          inputProps={{
                            placeholder: f.placeholder,
                            semanticId: `multi-section-filter-date-${f.name}`,
                          }}
                          calendarDensity='production'
                        />
                      );
                    }
                    if (f.type === 'select' || f.type === 'asyncSelect') {
                      const opts = (f.options ?? []) as Array<{
                        label: string;
                        value: string | number | boolean;
                      }>;
                      const selectOpts = opts.map((o) => ({
                        label: o.label,
                        value: o.value,
                      }));
                      const currentVal =
                        selectOpts.find((o) => o.value === filterValues[f.name]) ?? null;
                      return (
                        <Select
                          key={f.name}
                          name={f.name}
                          placeholder={f.placeholder}
                          options={selectOpts}
                          value={currentVal}
                          onChange={(selected: any) =>
                            handleFilterChange(f.name, selected?.value ?? undefined)
                          }
                          isClearable
                          semanticId={`multi-section-filter-select-${f.name}`}
                        />
                      );
                    }
                    return null;
                  })}
                </Filter.Grid>
              </Filter.Panel>
            </Filter.Root>
          )}
          <button
            type='button'
            className='flex items-center gap-1 bg-transparent border-0 cursor-pointer whitespace-nowrap'
            style={{ color: 'var(--ui-text-muted)', fontSize: '1.4rem', padding: '4px 8px' }}
            onClick={handleResetFilters}>
            <Icon name='settings_backup_restore' size='sm' />
            <Typography variant='body-s'>Reset Filter</Typography>
          </button>
        </div>
        {hasToolbar && (
          <div className='ml-auto'>
            {sc.toolbar.primary && (
              <ToolbarDropdownButton
                config={sc.toolbar.primary}
                navigate={navigate}
              />
            )}
          </div>
        )}
      </div>

      {hasImport && (
        <div
          className='flex justify-between items-center rounded-md border p-4 mb-6 gap-3'
          style={{ background: 'var(--ui-surface)', borderColor: 'var(--ui-border-muted)' }}>
          <Typography variant='body-s'>
            Quick &amp; easy to use CSV template to import large data in an instant.
          </Typography>
          <div className='flex gap-2'>
            {sc.import?.downloadTemplate && (
              <Button
                variant='secondary'
                label={sc.import.downloadTemplate.label ?? 'Download Template'}
                icon='cloud_upload'
                onClick={() => {
                  if (sc.import?.downloadTemplate?.endpoint && axiosInstance) {
                    axiosInstance
                      .post(sc.import.downloadTemplate.endpoint, {}, { responseType: 'blob' })
                      .then((res: any) => {
                        const url = window.URL.createObjectURL(new Blob([res.data ?? res]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${normalized.title ?? 'import'}_template.xlsx`;
                        link.click();
                        window.URL.revokeObjectURL(url);
                      })
                      .catch(() => {
                        toastify.error(
                          <Toast label='Error' message='Failed to download template' />
                        );
                      });
                  }
                }}
              />
            )}
            {sc.import?.uploadAction && (
              <div className='relative'>
                <input
                  className='absolute inset-0 opacity-0 w-full h-full cursor-pointer'
                  id='my-file-multiSectionImport'
                  type='file'
                  accept='.xlsx, .xls'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (!file || !sc.import?.uploadAction?.endpoint || !axiosInstance) return;
                    const formData = new FormData();
                    formData.append('File', file);
                    axiosInstance
                      .post(sc.import.uploadAction.endpoint, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      })
                      .then(() => {
                        toastify.success(
                          <Toast label='Success' message='Import completed successfully' />
                        );
                      })
                      .catch(() => {
                        toastify.error(
                          <Toast label='Error' message='Import failed' />
                        );
                      });
                    e.target.value = '';
                  }}
                />
                <Button
                  variant='secondary-outline'
                  label={sc.import.uploadAction.label ?? 'Import'}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div id='multiSectionCollapsible'>
        {normalized.sections.every(
          (s) => s.permission && checkPermission && !checkPermission(s.permission),
        ) && (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <Typography variant='body' color='var(--ui-text-muted)'>
              You do not have permission to view any awards.
            </Typography>
          </div>
        )}

        {normalized.sections.map((section) => {
          if (section.permission && checkPermission && !checkPermission(section.permission)) {
            return null;
          }
          const isOpen = openSections.has(section.id);
          return (
            <div key={section.id} className='mb-2'>
              <button
                type='button'
                className='flex items-center gap-2 w-full p-3 cursor-pointer border-0 rounded'
                style={{
                  background: 'var(--ui-background-muted)',
                  color: 'var(--ui-text)',
                }}
                onClick={() => toggleSection(section.id)}>
                <Icon
                  name={isOpen ? 'expand_less' : 'expand_more'}
                  size='md'
                />
                <Typography variant='body' fontWeight={600} id={`section-${section.id}`}>
                  {section.title}
                </Typography>
              </button>

              {isOpen && (
                <div className='mb-8 mt-2'>
                  <DynamicTableV2Component
                    schema={section.tableSchema as unknown as ExternalTableSchema}
                    axiosInstance={axiosInstance}
                    navigate={navigate}
                    checkPermission={checkPermission}
                    onActionIntercept={onActionIntercept}
                    envTokens={envTokens}
                    _sharedSearchText={searchText}
                    _sharedQueryParams={sharedQueryParams}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
